import React, { useState } from 'react';
import { Button } from '../ui/button.js';
import { Card, CardContent } from '../ui/card.js';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: string;
}

export const AssistantPanel: React.FC = () => {
  const [input, setInput] = useState('');
  const [useGPT, setUseGPT] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  const onSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      text: input,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/bees/AssistantBee/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: input,
          useChatGPT: useGPT
        })
      });
      
      const result = await response.json();
      
      const assistantMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        text: result.text || 'No response received',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Assistant API error:', error);
      const errorMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        text: 'Error: Failed to get response from assistant',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto p-4 space-y-2">
        {messages.map((m) => (
          <Card key={m.id} className={m.role === 'assistant' ? 'bg-blue-50' : ''}>
            <CardContent className="p-3">
              <p><strong>{m.role}:</strong> {m.text}</p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(m.timestamp).toLocaleTimeString()}
              </p>
            </CardContent>
          </Card>
        ))}
        {isLoading && (
          <Card className="bg-gray-50">
            <CardContent className="p-3">
              <p><strong>assistant:</strong> <em>Thinking...</em></p>
            </CardContent>
          </Card>
        )}
      </div>
      <div className="p-4 border-t flex flex-col space-y-2">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={useGPT}
            onChange={(e) => setUseGPT(e.target.checked)}
            id="use-gpt-checkbox"
          />
          <label htmlFor="use-gpt-checkbox" className="text-sm">Force ChatGPT</label>
        </div>
        <div className="flex space-x-2">
          <textarea
            className="flex-1 border rounded p-2 resize-none"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask the Assistant…"
            disabled={isLoading}
          />
          <Button 
            onClick={onSend} 
            disabled={!input.trim() || isLoading}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};
